/**
 * Prompt Deletion Manager
 *
 * Handles validation and execution of prompt deletion operations for line tools.
 * Provides smart deletion that:
 * - Validates resulting shapes
 * - Highlights prompts during selection
 * - Connects boundary prompts when consecutive prompts are removed
 */

import { LinePrompt } from './types';
import { ShapeBuilder } from './ShapeBuilder';
import { LinePromptVisualizer } from './LinePromptVisualizer';
import { PromptAligner } from './PromptAligner';
import { DensitySnapper } from './DensitySnapper';
import { Line2 } from 'three/examples/jsm/lines/Line2.js';
import { LineMaterial } from 'three/examples/jsm/lines/LineMaterial.js';
import { LineGeometry } from 'three/examples/jsm/lines/LineGeometry.js';
import * as THREE from 'three';

/** Green color for the deletion preview shape */
const PREVIEW_SHAPE_COLOR = 0x4caf50;

export interface DeletionValidationResult {
  valid: boolean;
  message: string;
  /** Warning message shown to user when deletion will break the shape but is still allowed */
  warning: string;
  /** Bridge prompts to add when deletion would otherwise create invalid shape */
  bridgePrompts: LinePrompt[];
  /** When boundary prompts are collinear, merge them into one - this is the merged prompt */
  mergedPrompt: LinePrompt | null;
  /** IDs of prompts to remove when merging (the two boundary prompts) */
  promptsToRemove: string[];
}

export interface ConsecutiveChainInfo {
  isConsecutive: boolean;
  /** The remaining prompts that border the deleted chain (not the deleted prompts themselves) */
  boundaryPrompts: { start: LinePrompt | null; end: LinePrompt | null };
  /** The connection points where the boundary prompts connected to the deleted chain */
  bridgePoints: { start: THREE.Vector3 | null; end: THREE.Vector3 | null };
}

export class PromptDeletionManager {
  private highlightedForDeletion: Set<string> = new Set();
  private validationShapeBuilder: ShapeBuilder; // Separate instance for validation to avoid side effects
  private previewGroup: THREE.Group | null = null;
  /** Cached validation result from the last validateDeletion call */
  private lastValidation: DeletionValidationResult | null = null;
  /** Selected prompt IDs from the last validation (to check if we can reuse the cache) */
  private lastValidationPromptIds: Set<string> | null = null;

  constructor(
    private scene: THREE.Scene,
    private renderArea: HTMLElement,
    private visualizer: LinePromptVisualizer,
    private promptAligner: PromptAligner,
    private densitySnapper: DensitySnapper,
    private getSnapContext: () => {
      densityMap: ImageData | null;
      positionMap: ImageData | null;
      floorHeight: number | null;
    },
    coordinateTransformer?: import('../utils/CoordinateTransformer').CoordinateTransformer,
  ) {
    // Create a dedicated ShapeBuilder for validation - keeps validation isolated.
    // Threading the transformer makes validation operate in the active base-plane
    // local frame so door/window strip prompts test their polygon validity in
    // wall-local coords (matches the main shape builder's behavior).
    this.validationShapeBuilder = new ShapeBuilder();
    if (coordinateTransformer) {
      this.validationShapeBuilder.setCoordinateTransformer(coordinateTransformer);
    }
  }

  /**
   * Get the set of currently highlighted prompt IDs
   */
  getHighlightedPromptIds(): Set<string> {
    return this.highlightedForDeletion;
  }

  /**
   * Update the highlighting of prompts in a selection box during right-drag.
   * Returns the selected prompts and validation result.
   */
  updateSelectionHighlight(linePrompts: LinePrompt[], selectionStart: THREE.Vector2, selectionEnd: THREE.Vector2, hoveredPromptId: string | null, skipGuards = false): { selectedPrompts: LinePrompt[]; validation: DeletionValidationResult } {
    const selectedPrompts = this.promptAligner.getPromptsInScreenBox(linePrompts, selectionStart, selectionEnd);

    // Update highlighting
    this.highlightedForDeletion.clear();
    for (const prompt of selectedPrompts) {
      this.highlightedForDeletion.add(prompt.id);
    }

    // Debug: Check for duplicate IDs in linePrompts
    const idCounts = new Map<string, number>();
    for (const p of linePrompts) {
      idCounts.set(p.id, (idCounts.get(p.id) || 0) + 1);
    }
    for (const [id, count] of idCounts) {
      if (count > 1) {
        console.warn(`[PromptDeletionManager] Duplicate prompt ID found: "${id}" appears ${count} times`);
      }
    }

    // Update visualizer with highlighted prompts
    this.visualizer.updatePromptVisuals(linePrompts, hoveredPromptId, this.highlightedForDeletion);

    // Validate deletion
    const validation = this.validateDeletion(selectedPrompts, linePrompts, skipGuards);

    return { selectedPrompts, validation };
  }

  /**
   * Clear all deletion highlighting and preview shape
   */
  clearHighlighting(linePrompts: LinePrompt[], hoveredPromptId: string | null): void {
    if (this.highlightedForDeletion.size > 0) {
      this.highlightedForDeletion.clear();
      this.visualizer.updatePromptVisuals(linePrompts, hoveredPromptId, null);
    }
    this.clearPreviewShape();
  }

  /**
   * Render a preview of the resulting shape outline in green (lines only, no fill)
   */
  private renderPreviewShape(vertices: THREE.Vector3[]): void {
    this.clearPreviewShape();

    if (vertices.length < 3) return;

    // Use first vertex as local origin to avoid precision issues
    const localOrigin = vertices[0].clone();
    const localVertices = vertices.map((v) => v.clone().sub(localOrigin));

    // Create group positioned at the local origin
    this.previewGroup = new THREE.Group();
    this.previewGroup.position.copy(localOrigin);

    // Create outline using Line2 in local coordinates
    const positions: number[] = [];
    for (const vertex of localVertices) {
      positions.push(vertex.x, vertex.y, vertex.z);
    }
    // Close the loop
    positions.push(localVertices[0].x, localVertices[0].y, localVertices[0].z);

    const lineGeometry = new LineGeometry();
    lineGeometry.setPositions(positions);

    const lineMaterial = new LineMaterial({
      color: PREVIEW_SHAPE_COLOR,
      linewidth: 2,
      resolution: new THREE.Vector2(this.renderArea.clientWidth, this.renderArea.clientHeight),
      depthTest: true,
      transparent: true,
      opacity: 0.8,
    });

    const line = new Line2(lineGeometry, lineMaterial);
    line.renderOrder = 996;
    this.previewGroup.add(line);

    // Add to scene
    this.scene.add(this.previewGroup);
  }

  /**
   * Render the snapped bridge/merged prompt line plus two connector lines
   * from the snapped endpoints back to the original bridge points.
   * The connector lines are preview-only and show how the snapped prompt
   * connects back to the remaining shape.
   */
  private renderBridgeWithConnectors(snappedPrompt: LinePrompt, originalBridgeStart: THREE.Vector3, originalBridgeEnd: THREE.Vector3): void {
    this.clearPreviewShape();

    // Compute the snapped prompt's actual endpoints
    const snappedNegEnd = snappedPrompt.worldPoint.clone().sub(snappedPrompt.direction.clone().multiplyScalar(snappedPrompt.negativeLength ?? 0));
    const snappedPosEnd = snappedPrompt.worldPoint.clone().add(snappedPrompt.direction.clone().multiplyScalar(snappedPrompt.positiveLength ?? 0));

    // Use snapped negative end as local origin to avoid precision issues
    const localOrigin = snappedNegEnd.clone();

    this.previewGroup = new THREE.Group();
    this.previewGroup.position.copy(localOrigin);

    const resolution = new THREE.Vector2(this.renderArea.clientWidth, this.renderArea.clientHeight);

    // Helper to add a Line2 segment to the preview group
    const addLine = (start: THREE.Vector3, end: THREE.Vector3, linewidth: number, opacity: number): void => {
      const localStart = start.clone().sub(localOrigin);
      const localEnd = end.clone().sub(localOrigin);
      const positions = [localStart.x, localStart.y, localStart.z, localEnd.x, localEnd.y, localEnd.z];

      const geometry = new LineGeometry();
      geometry.setPositions(positions);

      const material = new LineMaterial({
        color: PREVIEW_SHAPE_COLOR,
        linewidth,
        resolution,
        depthTest: true,
        transparent: true,
        opacity,
      });

      const line = new Line2(geometry, material);
      line.renderOrder = 996;
      if (this.previewGroup) {
        this.previewGroup.add(line);
      }
    };

    // Main snapped bridge line (solid, prominent)
    addLine(snappedNegEnd, snappedPosEnd, 3, 0.9);

    // Connector lines from snapped ends to original bridge points (thinner, more transparent)
    addLine(snappedNegEnd, originalBridgeStart, 2, 0.6);
    addLine(snappedPosEnd, originalBridgeEnd, 2, 0.6);

    this.scene.add(this.previewGroup);
  }

  /**
   * Apply density snapping (position + rotation) to a prompt using the current snap context.
   */
  private applyDensitySnap(prompt: LinePrompt): void {
    const { densityMap, positionMap, floorHeight } = this.getSnapContext();
    this.densitySnapper.alignPromptToPositionAndRotation(prompt, densityMap, positionMap, floorHeight);
  }

  /**
   * Clear the preview shape
   */
  private clearPreviewShape(): void {
    if (this.previewGroup) {
      this.scene.remove(this.previewGroup);
      this.previewGroup.traverse((obj) => {
        if (obj instanceof THREE.Mesh || obj instanceof THREE.Line || obj instanceof Line2) {
          obj.geometry.dispose();
          if (Array.isArray(obj.material)) {
            obj.material.forEach((mat) => mat.dispose());
          } else {
            obj.material.dispose();
          }
        }
      });
      this.previewGroup = null;
    }
  }

  /**
   * Validate if deleting the selected prompts would result in a valid shape.
   * - If remaining prompts form a valid shape → show the shape preview
   * - If remaining prompts don't form a valid shape but deletion is consecutive →
   *   create a bridge prompt and show the bridge line preview
   * - If deletion is non-consecutive or can't be fixed → show error (or warning if skipGuards)
   *
   * @param skipGuards When true, deletion is always allowed but with warnings instead of blocking.
   *                   Used by ManualLineMode where the user has full control.
   */
  validateDeletion(selectedPrompts: LinePrompt[], allPrompts: LinePrompt[], skipGuards = false): DeletionValidationResult {
    const selectedIds = new Set(selectedPrompts.map((p) => p.id));

    // Helper to cache and return result
    const cacheAndReturn = (result: DeletionValidationResult): DeletionValidationResult => {
      this.lastValidation = result;
      this.lastValidationPromptIds = selectedIds;
      return result;
    };
    const remainingPrompts = allPrompts.filter((p) => !selectedIds.has(p.id));

    const noChange: DeletionValidationResult = {
      valid: true,
      message: '',
      warning: '',
      bridgePrompts: [],
      mergedPrompt: null,
      promptsToRemove: [],
    };

    const invalid = (message: string): DeletionValidationResult =>
      cacheAndReturn({
        valid: false,
        message,
        warning: '',
        bridgePrompts: [],
        mergedPrompt: null,
        promptsToRemove: [],
      });

    const withWarning = (warning: string): DeletionValidationResult =>
      cacheAndReturn({
        valid: true,
        message: '',
        warning,
        bridgePrompts: [],
        mergedPrompt: null,
        promptsToRemove: [],
      });

    // Check if the original shape is valid (only when there are non-temporary prompts)
    const allRegularPromptsForCheck = allPrompts.filter((p) => !p.isTemporary);
    let originalShapeValid = false;
    if (allRegularPromptsForCheck.length >= 3) {
      const origCopies = this.createPromptCopies(allRegularPromptsForCheck);
      const origShape = this.validationShapeBuilder.createShapeFromPrompts(origCopies);
      originalShapeValid = origShape !== null && origShape.vertices.length >= 3;
    }

    /**
     * Return an error when guards are active, or a warning when guards are
     * skipped. BUT when the shape was ALREADY broken before this deletion,
     * there is nothing to protect — deletion can't "break" an invalid shape
     * further, so always allow it (with a warning). This lets the user prune
     * prompts to fix a shape that's already invalid.
     */
    const guardOrWarn = (message: string): DeletionValidationResult => {
      if (skipGuards || !originalShapeValid) {
        this.clearPreviewShape();
        return withWarning(message);
      }
      return invalid(message);
    };

    /**
     * Verify that a candidate result (with bridges/merges) actually produces a valid shape.
     * If the original shape was valid but the result would be invalid, return guardOrWarn instead.
     * Returns null if the result passes verification (caller should use the original result).
     */
    const verifyResultShape = (result: DeletionValidationResult): DeletionValidationResult | null => {
      if (!originalShapeValid) return null; // Original wasn't valid, skip check
      if (!result.valid) return null; // Already invalid, nothing to verify

      // Simulate the final prompt list after applying bridges/merges
      const removeIds = new Set([...selectedIds, ...result.promptsToRemove]);
      const simulatedPrompts = allRegularPromptsForCheck
        .filter((p) => !removeIds.has(p.id))
        .concat(result.bridgePrompts)
        .concat(result.mergedPrompt ? [result.mergedPrompt] : []);

      if (simulatedPrompts.length < 3) {
        this.clearPreviewShape();
        return guardOrWarn('Shape will break');
      }

      const copies = this.createPromptCopies(simulatedPrompts);
      const shape = this.validationShapeBuilder.createShapeFromPrompts(copies);
      if (!shape || shape.vertices.length < 3) {
        this.clearPreviewShape();
        return guardOrWarn('Shape will break');
      }

      return null; // Verification passed
    };

    // Temporary prompts can always be deleted without shape validation
    const allSelectedAreTemporary = selectedPrompts.every((p) => p.isTemporary);
    if (allSelectedAreTemporary) {
      this.clearPreviewShape();
      return cacheAndReturn(noChange);
    }

    // If deleting all prompts, that's always valid (resets to initial state)
    if (remainingPrompts.length === 0) {
      this.clearPreviewShape();
      return cacheAndReturn(noChange);
    }

    // For mixed selections, only validate against the non-temporary prompts
    // Temporary prompts don't participate in the main shape
    const remainingRegularPrompts = remainingPrompts.filter((p) => !p.isTemporary);

    // Need at least 3 remaining regular lines for a valid shape
    if (remainingRegularPrompts.length < 3) {
      this.clearPreviewShape();
      return guardOrWarn('Shape will break');
    }

    // Try to build shape from remaining regular prompts first
    // If the shape is valid, allow the deletion regardless of consecutiveness
    const promptCopies = this.createPromptCopies(remainingRegularPrompts);
    const shape = this.validationShapeBuilder.createShapeFromPrompts(promptCopies);

    if (shape && shape.vertices.length >= 3) {
      // Shape is valid with remaining prompts, but still check if boundary prompts
      // are collinear and should be merged (collinear prompts cause issues downstream)
      const selectedRegularPromptsEarly = selectedPrompts.filter((p) => !p.isTemporary);
      const allRegularPromptsEarly = allPrompts.filter((p) => !p.isTemporary);
      const consecutiveInfoEarly = this.getConsecutiveChainInfo(selectedRegularPromptsEarly, allRegularPromptsEarly);

      if (consecutiveInfoEarly.isConsecutive) {
        const mergeResult = this.tryMergeCollinearBoundaryPrompts(consecutiveInfoEarly, allPrompts);
        if (mergeResult) {
          const rejected = verifyResultShape(mergeResult);
          if (rejected) return rejected;
          // Shape is valid, just merging collinear prompts - show the shape preview
          this.renderPreviewShape(shape.vertices);
          return cacheAndReturn(mergeResult);
        }
      }

      // No collinear merge needed - show the full shape preview
      this.renderPreviewShape(shape.vertices);
      return cacheAndReturn(noChange);
    }

    // Shape is invalid with remaining prompts alone
    // Check if this is a consecutive deletion - only then can we bridge/merge
    // Only consider non-temporary selected prompts for chain analysis
    const selectedRegularPrompts = selectedPrompts.filter((p) => !p.isTemporary);
    const allRegularPrompts = allPrompts.filter((p) => !p.isTemporary);
    const consecutiveInfo = this.getConsecutiveChainInfo(selectedRegularPrompts, allRegularPrompts);

    if (!consecutiveInfo.isConsecutive) {
      // Non-consecutive deletion that doesn't result in valid shape
      this.clearPreviewShape();
      return guardOrWarn('Shape will break');
    }

    // When guards are active (skipGuards=false), single prompts are also treated as chains
    // and get bridge/merge logic. When guards are skipped (manual mode), require 2+ prompts.
    if (skipGuards && selectedRegularPrompts.length < 2) {
      // Single line deletion that breaks the shape (manual mode only)
      this.clearPreviewShape();
      return guardOrWarn('Shape will break');
    }

    // Check if we can create a bridge between the boundary prompts
    const { boundaryPrompts, bridgePoints } = consecutiveInfo;
    if (boundaryPrompts.start && boundaryPrompts.end && bridgePoints.start && bridgePoints.end) {
      // Try to merge collinear boundary prompts first
      const mergeResult = this.tryMergeCollinearBoundaryPrompts(consecutiveInfo, allPrompts);
      if (mergeResult) {
        // Build shape with merged prompt for preview
        const promptsForPreview = remainingRegularPrompts.filter((p) => !mergeResult.promptsToRemove.includes(p.id)).concat(mergeResult.mergedPrompt ? [mergeResult.mergedPrompt] : []);
        const previewCopies = this.createPromptCopies(promptsForPreview);
        const mergedShape = this.validationShapeBuilder.createShapeFromPrompts(previewCopies);
        const rejected = verifyResultShape(mergeResult);
        if (rejected) return rejected;
        if (mergedShape && mergedShape.vertices.length >= 3) {
          this.renderPreviewShape(mergedShape.vertices);
        } else {
          this.clearPreviewShape();
        }
        return cacheAndReturn(mergeResult);
      }

      // Not collinear - create a bridge prompt to connect the gap
      const bridgePrompt = this.createBridgePrompt(bridgePoints.start, bridgePoints.end, boundaryPrompts.start, boundaryPrompts.end, allPrompts);

      // Density-snap the bridge prompt to the wall
      this.applyDensitySnap(bridgePrompt);

      // Check each side: if the snapped bridge is not collinear with the neighbor,
      // create an extra bridge prompt to connect the snapped endpoint to the original bridge point
      const snappedNegEnd = bridgePrompt.worldPoint.clone().sub(bridgePrompt.direction.clone().multiplyScalar(bridgePrompt.negativeLength ?? 0));
      const snappedPosEnd = bridgePrompt.worldPoint.clone().add(bridgePrompt.direction.clone().multiplyScalar(bridgePrompt.positiveLength ?? 0));

      // Start side: check if connector (snapped neg end → original bridge start) is collinear with start neighbor
      const startDist = snappedNegEnd.distanceTo(bridgePoints.start);
      const startSmallEnough = startDist <= 0.01;
      let startCollinear = startSmallEnough;
      if (!startSmallEnough) {
        const startConnectorDir = new THREE.Vector3().subVectors(bridgePoints.start, snappedNegEnd).normalize();
        startCollinear = Math.abs(startConnectorDir.dot(boundaryPrompts.start.direction)) > PromptAligner.ALIGN_PARALLEL_COS;
      }

      // End side: check if connector (snapped pos end → original bridge end) is collinear with end neighbor
      const endDist = snappedPosEnd.distanceTo(bridgePoints.end);
      const endSmallEnough = endDist <= 0.01;
      let endCollinear = endSmallEnough;
      if (!endSmallEnough) {
        const endConnectorDir = new THREE.Vector3().subVectors(bridgePoints.end, snappedPosEnd).normalize();
        endCollinear = Math.abs(endConnectorDir.dot(boundaryPrompts.end.direction)) > PromptAligner.ALIGN_PARALLEL_COS;
      }

      // If BOTH sides are collinear (or small enough), we still need the bridge but without extra legs
      if (startCollinear && endCollinear) {
        const bridgeResult: DeletionValidationResult = {
          valid: true,
          message: '',
          warning: '',
          bridgePrompts: [bridgePrompt],
          mergedPrompt: null,
          promptsToRemove: [],
        };
        const rejected = verifyResultShape(bridgeResult);
        if (rejected) return rejected;
        // Show the bridge preview and return with just the main bridge (no extra legs)
        this.renderBridgeWithConnectors(bridgePrompt, bridgePoints.start, bridgePoints.end);
        return cacheAndReturn(bridgeResult);
      }

      // At least one side needs adjustment - skip density snap and just connect directly
      const simpleBridge = this.createBridgePrompt(bridgePoints.start, bridgePoints.end, boundaryPrompts.start, boundaryPrompts.end, allPrompts);

      const simpleBridgeResult: DeletionValidationResult = {
        valid: true,
        message: '',
        warning: '',
        bridgePrompts: [simpleBridge],
        mergedPrompt: null,
        promptsToRemove: [],
      };
      const rejected = verifyResultShape(simpleBridgeResult);
      if (rejected) return rejected;
      // Show the simple bridge preview (no density snap, just direct connection)
      this.renderBridgeWithConnectors(simpleBridge, bridgePoints.start, bridgePoints.end);
      return cacheAndReturn(simpleBridgeResult);
    }

    // Can't determine bridge points
    this.clearPreviewShape();
    return guardOrWarn('Shape will break');
  }

  /**
   * Create a bridge prompt that connects two crossing points.
   * The prompt is placed at the midpoint with direction along the bridge line.
   * Connections are pre-set so visualization renders the line correctly before ShapeBuilder processes it.
   */
  private createBridgePrompt(bridgeStart: THREE.Vector3, bridgeEnd: THREE.Vector3, _boundaryPromptStart: LinePrompt, _boundaryPromptEnd: LinePrompt, allPrompts: LinePrompt[]): LinePrompt {
    // Calculate midpoint between the two bridge points
    const midpoint = new THREE.Vector3().addVectors(bridgeStart, bridgeEnd).multiplyScalar(0.5);

    // Direction is along the bridge line (from start to end)
    const direction = new THREE.Vector3().subVectors(bridgeEnd, bridgeStart).normalize();

    // Calculate lengths to reach the bridge points from the midpoint
    const halfLength = bridgeStart.distanceTo(bridgeEnd) / 2;

    // Generate unique ID by finding max existing bridge number and incrementing
    let maxBridgeNum = 0;
    for (const p of allPrompts) {
      const match = p.id.match(/^bridge_(\d+)$/);
      if (match) {
        maxBridgeNum = Math.max(maxBridgeNum, parseInt(match[1], 10));
      }
    }

    return {
      id: `bridge_${maxBridgeNum + 1}`,
      worldPoint: midpoint,
      direction: direction,
      screenPosition: new THREE.Vector2(0, 0), // Will be updated when rendered
      positiveLength: halfLength,
      negativeLength: halfLength,
      // Pre-set connections so visualization renders the bridge line correctly
      // These will be updated by ShapeBuilder when the shape is rebuilt
      connections: {
        positive: {
          targetPromptIndex: -1, // Placeholder, will be updated by ShapeBuilder
          targetSide: 'pos',
          point: bridgeEnd.clone(),
        },
        negative: {
          targetPromptIndex: -1, // Placeholder, will be updated by ShapeBuilder
          targetSide: 'neg',
          point: bridgeStart.clone(),
        },
      },
    };
  }

  /**
   * Check if boundary prompts are collinear (parallel AND on the same line) and merge them if so.
   * Returns the merged validation result, or null if not collinear.
   */
  private tryMergeCollinearBoundaryPrompts(consecutiveInfo: ConsecutiveChainInfo, allPrompts: LinePrompt[]): DeletionValidationResult | null {
    const { boundaryPrompts, bridgePoints } = consecutiveInfo;

    if (!boundaryPrompts.start || !boundaryPrompts.end || !bridgePoints.start || !bridgePoints.end) {
      return null;
    }

    const dirA = boundaryPrompts.start.direction;
    const dirB = boundaryPrompts.end.direction;
    const absDotAB = Math.abs(dirA.dot(dirB));

    // Check if boundary prompts are parallel to each other
    const boundaryPromptsParallel = absDotAB > PromptAligner.ALIGN_PARALLEL_COS;
    if (!boundaryPromptsParallel) {
      return null;
    }

    // Check that the boundary prompts are on the same line (not just parallel)
    // by measuring perpendicular distance from each prompt's worldPoint to the other's line
    const vecAtoB = new THREE.Vector3().subVectors(boundaryPrompts.end.worldPoint, boundaryPrompts.start.worldPoint);
    const distAtoLineB = vecAtoB.clone().cross(dirB).length(); // Perpendicular distance from A to line B
    const distBtoLineA = vecAtoB.clone().cross(dirA).length(); // Perpendicular distance from B to line A

    // Use a fixed tolerance for perpendicular distance - prompts must be very close to the same line
    // 5cm tolerance to account for minor floating point drift
    const maxPerpendicularDist = 0.05;

    const onSameLine = distAtoLineB < maxPerpendicularDist && distBtoLineA < maxPerpendicularDist;

    // Only merge if boundary prompts are collinear (parallel AND on the same line)
    if (!onSameLine) {
      return null;
    }

    // Merge collinear boundary prompts - no density snap needed since they're already aligned
    const mergedPrompt = this.createMergedPrompt(boundaryPrompts.start, boundaryPrompts.end, bridgePoints.start, bridgePoints.end, allPrompts);

    return {
      valid: true,
      message: '',
      warning: '',
      bridgePrompts: [],
      mergedPrompt,
      promptsToRemove: [boundaryPrompts.start.id, boundaryPrompts.end.id],
    };
  }

  /**
   * Create a merged prompt from two collinear boundary prompts.
   * The merged prompt has averaged direction and spans from the outer connection
   * points of both original prompts.
   */
  private createMergedPrompt(promptA: LinePrompt, promptB: LinePrompt, bridgePointA: THREE.Vector3, bridgePointB: THREE.Vector3, allPrompts: LinePrompt[]): LinePrompt {
    // Merged direction. Pre-fix this routed through
    // `PromptAligner.averageAngle` + `dir3DFromAngle`, which read only
    // `direction.x`/`direction.y` (treating the direction as a world-XY
    // angle) and reconstructed as `(cos, sin, 0)`. Identity for
    // horizontal-plane prompts; collapses to horizontal for vertical or
    // diagonal wall prompts (a vertical `(0,0,1)` direction has
    // `atan2(0,0) = 0`, which becomes `(1,0,0)`). The caller has
    // already verified `promptA` and `promptB` are collinear in 3D, so
    // averaging is unnecessary — just pick `promptA.direction`, flipped
    // if it points opposite to `promptB.direction`.
    const avgDirection = promptA.direction.clone();
    if (avgDirection.dot(promptB.direction) < 0) avgDirection.negate();

    // The merged prompt spans from bridgePointA to bridgePointB
    // Place the worldPoint at the midpoint between the bridge points
    const midpoint = new THREE.Vector3().addVectors(bridgePointA, bridgePointB).multiplyScalar(0.5);

    // Calculate lengths to each bridge point
    const halfLength = bridgePointA.distanceTo(bridgePointB) / 2;

    // Generate unique ID
    let maxMergedNum = 0;
    for (const p of allPrompts) {
      const match = p.id.match(/^merged_(\d+)$/);
      if (match) {
        maxMergedNum = Math.max(maxMergedNum, parseInt(match[1], 10));
      }
    }

    return {
      id: `merged_${maxMergedNum + 1}`,
      worldPoint: midpoint,
      direction: avgDirection,
      screenPosition: new THREE.Vector2(0, 0),
      positiveLength: halfLength,
      negativeLength: halfLength,
    };
  }

  /**
   * Create deep copies of prompts for validation
   */
  private createPromptCopies(prompts: LinePrompt[]): LinePrompt[] {
    return prompts.map((p) => ({
      ...p,
      worldPoint: p.worldPoint.clone(),
      direction: p.direction.clone(),
      screenPosition: p.screenPosition.clone(),
      connections: undefined,
    }));
  }

  /**
   * Execute deletion of prompts in a selection box.
   * Returns the new array of prompts after deletion, or null if deletion was invalid.
   * If a bridge prompt is needed to maintain a valid shape, it will be added.
   * If boundary prompts are collinear, they will be merged into one.
   */
  executeDelete(linePrompts: LinePrompt[], selectionStart: THREE.Vector2, selectionEnd: THREE.Vector2, hoveredPromptId: string | null, skipGuards = false, onShapeUpdate?: (prompts: LinePrompt[]) => void): LinePrompt[] | null {
    const selectedPrompts = this.promptAligner.getPromptsInScreenBox(linePrompts, selectionStart, selectionEnd);
    if (selectedPrompts.length === 0) {
      return null;
    }

    const selectedIds = new Set(selectedPrompts.map((p) => p.id));

    // Use cached validation if available and matches the current selection
    // This ensures the executed result matches what was previewed
    let validation: DeletionValidationResult;
    if (this.lastValidation && this.lastValidationPromptIds && this.lastValidationPromptIds.size === selectedIds.size && [...selectedIds].every((id) => this.lastValidationPromptIds?.has(id))) {
      validation = this.lastValidation;
    } else {
      validation = this.validateDeletion(selectedPrompts, linePrompts, skipGuards);
    }

    if (!validation.valid) {
      return null;
    }

    // Also remove prompts that need to be merged (boundary prompts when collinear)
    const removeIds = new Set([...selectedIds, ...validation.promptsToRemove]);

    // Remove the selected prompts and any prompts marked for removal
    let newPrompts = linePrompts.filter((p) => !removeIds.has(p.id));

    // Add bridge prompts if needed to maintain valid shape
    if (validation.bridgePrompts.length > 0) {
      newPrompts = [...newPrompts, ...validation.bridgePrompts];
    }

    // Add merged prompt if boundary prompts were collinear
    if (validation.mergedPrompt) {
      newPrompts = [...newPrompts, validation.mergedPrompt];
    }

    // Run edge cleanup around each bridge prompt (same merge logic as after drag)
    if (onShapeUpdate && validation.bridgePrompts.length > 0) {
      const { floorHeight } = this.getSnapContext();
      for (const bridge of validation.bridgePrompts) {
        if (newPrompts.includes(bridge)) {
          newPrompts = this.promptAligner.cleanupEdgesAroundPrompt(bridge, newPrompts, floorHeight, onShapeUpdate);
        }
      }
    }

    // Clear deletion highlighting
    this.clearHighlighting(newPrompts, hoveredPromptId);

    return newPrompts;
  }

  /**
   * Check if the selected prompts form a consecutive chain in the shape.
   * Returns info about the chain including the boundary prompts and the bridge points
   * (the original crossing points where the deleted chain connected to the remaining shape).
   */
  getConsecutiveChainInfo(selectedPrompts: LinePrompt[], allPrompts: LinePrompt[]): ConsecutiveChainInfo {
    if (selectedPrompts.length === 0) {
      return {
        isConsecutive: true,
        boundaryPrompts: { start: null, end: null },
        bridgePoints: { start: null, end: null },
      };
    }

    if (selectedPrompts.length === 1) {
      // Single prompt - find its neighbors and the connection points
      const prompt = selectedPrompts[0];
      const neighbors = this.getPromptNeighbors(prompt, allPrompts);

      // Get the connection points (where this prompt connected to its neighbors)
      const startPoint = prompt.connections?.positive?.point?.clone() ?? null;
      const endPoint = prompt.connections?.negative?.point?.clone() ?? null;

      return {
        isConsecutive: true,
        boundaryPrompts: { start: neighbors.positive, end: neighbors.negative },
        bridgePoints: { start: startPoint, end: endPoint },
      };
    }

    const selectedIds = new Set(selectedPrompts.map((p) => p.id));

    // Build adjacency from the selected prompts
    // Find prompts that have exactly one neighbor in the selection (boundary prompts of the deleted chain)
    const chainBoundaryPrompts: Array<{ prompt: LinePrompt; externalSide: 'positive' | 'negative' }> = [];

    // Count how many internal connections exist within the selection
    let internalConnectionCount = 0;

    for (const prompt of selectedPrompts) {
      const neighbors = this.getPromptNeighbors(prompt, allPrompts);
      let externalSide: 'positive' | 'negative' | null = null;

      if (neighbors.positive && selectedIds.has(neighbors.positive.id)) {
        internalConnectionCount++;
      } else if (neighbors.positive) {
        externalSide = 'positive';
      }

      if (neighbors.negative && selectedIds.has(neighbors.negative.id)) {
        internalConnectionCount++;
      } else if (neighbors.negative) {
        externalSide = 'negative';
      }

      // A prompt is on the boundary of the deleted chain if it has at least one external connection
      if (externalSide) {
        chainBoundaryPrompts.push({ prompt, externalSide });
      }
    }

    // Each internal connection is counted twice (once from each side), so divide by 2
    internalConnectionCount = internalConnectionCount / 2;

    // For a consecutive chain of N prompts, there should be exactly N-1 internal connections
    // (they form a linear chain). If there are fewer, the selection is not consecutive.
    const expectedInternalConnections = selectedPrompts.length - 1;
    const isConsecutive = internalConnectionCount >= expectedInternalConnections;

    // Find the unselected neighbors and the bridge points (connection points to the outside)
    let startNeighbor: LinePrompt | null = null;
    let endNeighbor: LinePrompt | null = null;
    let startBridgePoint: THREE.Vector3 | null = null;
    let endBridgePoint: THREE.Vector3 | null = null;

    if (chainBoundaryPrompts.length >= 1) {
      const { prompt: bp1, externalSide: side1 } = chainBoundaryPrompts[0];
      const neighbors1 = this.getPromptNeighbors(bp1, allPrompts);

      if (side1 === 'positive' && neighbors1.positive && !selectedIds.has(neighbors1.positive.id)) {
        startNeighbor = neighbors1.positive;
        startBridgePoint = bp1.connections?.positive?.point?.clone() ?? null;
      } else if (side1 === 'negative' && neighbors1.negative && !selectedIds.has(neighbors1.negative.id)) {
        startNeighbor = neighbors1.negative;
        startBridgePoint = bp1.connections?.negative?.point?.clone() ?? null;
      }
    }

    if (chainBoundaryPrompts.length >= 2) {
      const { prompt: bp2, externalSide: side2 } = chainBoundaryPrompts[1];
      const neighbors2 = this.getPromptNeighbors(bp2, allPrompts);

      if (side2 === 'positive' && neighbors2.positive && !selectedIds.has(neighbors2.positive.id)) {
        endNeighbor = neighbors2.positive;
        endBridgePoint = bp2.connections?.positive?.point?.clone() ?? null;
      } else if (side2 === 'negative' && neighbors2.negative && !selectedIds.has(neighbors2.negative.id)) {
        endNeighbor = neighbors2.negative;
        endBridgePoint = bp2.connections?.negative?.point?.clone() ?? null;
      }
    }

    return {
      isConsecutive,
      boundaryPrompts: { start: startNeighbor, end: endNeighbor },
      bridgePoints: { start: startBridgePoint, end: endBridgePoint },
    };
  }

  /**
   * Get the neighboring prompts (connected via positive and negative sides)
   */
  private getPromptNeighbors(prompt: LinePrompt, allPrompts: LinePrompt[]): { positive: LinePrompt | null; negative: LinePrompt | null } {
    let positive: LinePrompt | null = null;
    let negative: LinePrompt | null = null;

    if (prompt.connections?.positive) {
      const targetIdx = prompt.connections.positive.targetPromptIndex;
      if (targetIdx >= 0 && targetIdx < allPrompts.length) {
        positive = allPrompts[targetIdx];
      }
    }

    if (prompt.connections?.negative) {
      const targetIdx = prompt.connections.negative.targetPromptIndex;
      if (targetIdx >= 0 && targetIdx < allPrompts.length) {
        negative = allPrompts[targetIdx];
      }
    }

    return { positive, negative };
  }
}
